import React from "react";
import styles from "./IntegrationCard.module.css";

/**
 * Reusable IntegrationCard component
 * Displays a single integration with connect/disconnect button
 * 
 * Props:
 * - integration: integration definition object
 * - status: current status ("connected", "not connected", "error", "pending")
 * - onConnect: callback when Connect button is clicked
 * - onDisconnect: callback when Disconnect button is clicked
 * - isLoading: whether the card is in a loading state
 */
export function IntegrationCard({
  integration,
  status,
  onConnect,
  onDisconnect,
  isLoading = false,
}) {
  const connected = status === "connected";
  const isError = status === "error";
  const isPending = status === "pending";

  const handleClick = () => {
    if (connected) {
      onDisconnect?.(integration);
    } else if (integration.connectable) {
      onConnect?.(integration);
    }
  };

  const getStatusBadgeClass = () => {
    if (connected) return styles.connected;
    if (isError) return styles.error;
    return styles.notConnected;
  };

  const getButtonClass = () => {
    if (!integration.connectable) return styles.buttonComingSoon;
    if (connected) return styles.buttonDisconnect;
    return styles.buttonConnect;
  };

  return (
    <div
      className={styles.card}
      data-testid={`integration-card-${integration.id}`}
    >
      <div className={styles.icon} aria-hidden="true">
        {integration.icon}
      </div>
      <div className={styles.cardContent}>
        <h4>{integration.name}</h4>
        <span
          className={`${styles.statusBadge} ${getStatusBadgeClass()}`}
          aria-live="polite"
        >
          {connected ? "Connected" : isError ? "Connection error" : isPending ? "Connecting..." : "Not connected"}
        </span>
        <p className={styles.description}>{integration.description}</p>

        {integration.connectable && (
          <button
            className={`${styles.button} ${getButtonClass()}`}
            onClick={handleClick}
            disabled={isLoading || isPending}
            aria-pressed={connected}
            aria-label={`${connected ? "Disconnect" : "Connect"} ${integration.name}`}
          >
            {isLoading || isPending ? "Loading..." : connected ? "Disconnect" : "Connect"}
          </button>
        )}
        
        {!integration.connectable && (
          <button
            className={`${styles.button} ${styles.buttonComingSoon}`}
            disabled
            aria-label={`${integration.name} coming soon`}
          >
            Coming Soon
          </button>
        )}
      </div>
    </div>
  );
}

export default IntegrationCard;
